import React from 'react';

import { Footer } from 'components/footer/Footer';
import { Prismic_HomepageConnectionConnection, Prismic_HomepageLinks } from 'types/prismic';
import { asText, RichText } from 'components/rich-text/RichText';
import { linkResolver, withPreview } from 'gatsby-source-prismic-graphql';
import { StaticQuery, graphql } from 'gatsby';
import { linkableFragment } from 'graphql/linkableFragment';
import { pageFragment } from 'graphql/pageFragment';
import { articleFragment } from 'graphql/articleFragment';
import { projectFragment } from 'graphql/projectFragment';
import { metaFragment } from 'graphql/metaFragment';
import { associationFragment } from 'graphql/associationFragment';
import { PrismicRichText } from '../../types/prismicRichtext';
import { useIntl } from 'react-intl';
import { Link } from 'components/link/Link';

interface IFooterProps {
  prismic: { allHomepages: Prismic_HomepageConnectionConnection };
}

const FooterComponent = ({ prismic }: IFooterProps) => {
  const homepages = prismic?.allHomepages?.edges;

  const { locale } = useIntl();

  const findHomepage = homepages?.find(i => i?.node?._meta?.lang === locale) ?? null;

  if (!findHomepage) {
    return null;
  }

  const homepage = findHomepage.node;

  const date = new Date();

  const address = homepage?.address as PrismicRichText;
  const infoText = homepage?.info_text as PrismicRichText;
  const copyright = homepage?.copyright ? asText(homepage?.copyright) : undefined;
  const copyrightReplaced = copyright
    ? copyright.replace(/YEAR/g, date.getFullYear().toString())
    : undefined;
  const contactInfo = homepage?.contact_info as PrismicRichText;
  const links = homepage?.links ?? [];

  const signupLabel = homepage?.newsletter_title ? asText(homepage?.newsletter_title) : undefined;
  const signupPlaceholder = homepage?.placeholder ? asText(homepage?.placeholder) : undefined;
  const signupButton = homepage?.signup_button ? asText(homepage?.signup_button) : undefined;

  return (
    <Footer
      address={address ? <RichText>{address}</RichText> : undefined}
      contactLink={infoText ? <RichText>{infoText}</RichText> : undefined}
      copyright={copyrightReplaced}
      tel={contactInfo ? <RichText>{contactInfo}</RichText> : undefined}
      signupLabel={signupLabel}
      signupPlaceholder={signupPlaceholder}
      signupButton={signupButton}
    >
      {links.map((link: Prismic_HomepageLinks, i: number) => (
        <Link key={i} to={linkResolver(link.link_link)}>
          {asText(link.link_title)}
        </Link>
      ))}
    </Footer>
  );
};

const footerQuery = graphql`
  query FooterQuery {
    prismic {
      allHomepages {
        edges {
          node {
            _meta {
              lang
            }
            address
            info_text
            links {
              link_title
              link_link {
                ...linkable
              }
            }
            copyright
            contact_info
            newsletter_title
            placeholder
            signup_button
          }
        }
      }
    }
  }
`;

export const FooterContainer = () => {
  return (
    <StaticQuery
      query={footerQuery}
      render={withPreview(FooterComponent, footerQuery, [
        linkableFragment,
        pageFragment,
        articleFragment,
        projectFragment,
        metaFragment,
        associationFragment,
      ])}
    />
  );
};
